import {
  AlertTriangle,
  Check,
  CheckCheck,
  ChevronDown,
  CircleHelp,
  Eye,
  GitFork,
  Link2,
  PenLine,
  Pencil,
  SpellCheck,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import {
  ApiError,
  type Contradiction,
  type ContradictionAction,
  type ContradictionBelief,
  type OwnerQuestion,
  type PendingCorrection,
  type ReviewInsight,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate, formatTimecode } from "@/lib/format";
import { categoryLabel, categoryMeta } from "@/lib/insightCategory";
import { describeUngrounded } from "@/lib/ungrounded";
import { ApproveSourceBar } from "@/components/ApproveSourceBar";
import { QuestionAnswer } from "@/components/QuestionAnswer";

/**
 * INS-4 and AUTO-1 — the review queue, where nothing becomes truth until the owner says so.
 *
 * Two kinds of card, and what they have in common is the reason they are on one screen: both
 * are things that cannot be decided without a human.
 *
 * **Insights.** Everything extraction produced lands `pending` and does nothing — not
 * embedded, not searchable, not in the world view — until it is approved here. Behind "How
 * this was read" is the step breakdown the owner learns to trust or distrust the extractor
 * from. Since INS-7 (M17.4) one source's cards sit together, in the order they were read out of it,
 * and beneath the last of them the owner can approve that whole source at once — a meeting of
 * thirteen insights, read top to bottom, is one decision rather than thirteen.
 *
 * **Corrections** (M13.1). A wording fix the `Reasoner` proposed and was not confident enough
 * to apply on its own (SPCH-8). These were on Transcript Review alone until the merged
 * Needs-you stream needed a read across every recording — and once one existed, keeping them
 * off this screen was the queue quietly not holding one of the three things that cannot be
 * decided without a human. Transcript Review is still where a proposal is read *in context*,
 * and both screens resolve through the same call, so answering one empties the other.
 *
 * **Contradictions** (M7.3). Two current beliefs on one subject that disagree, either because
 * an insight disputed one and synthesis could not tell which won, or because the scheduled
 * sweep found two that had drifted apart across separate batches. This is the *only* place
 * one can be resolved: the World view navigator's "Needs you → Contradictions" row is a count
 * that navigates here, and there is deliberately no resolve control on a subject page — a
 * belief pair answerable from two screens is a belief pair that gets answered twice.
 *
 * **Questions** (M17.3, UI-21). The system asking the owner for something it noticed is missing —
 * who a page is about, what a person does, who leads an initiative. Last on the screen, as they are
 * last in the Needs-you order: a question is never blocking, and answering the cards above it can
 * fill the very gap it noticed. Each is answered by the write a page already makes.
 *
 * The fourth thing a human must decide — who a voice is (SPCH-6) — is not here, and that is
 * not an oversight: naming a voice means listening to it, so full mode asks on the recording's
 * own Speakers tab, where the rest of the meeting is. Simple mode's one stream merges all four
 * because it has no recording detail to send anybody to (UI-16).
 */
export function ReviewQueuePage() {
  const { api, refreshReview } = useApp();
  const [params] = useSearchParams();

  const [insights, setInsights] = useState<ReviewInsight[] | null>(null);
  const [corrections, setCorrections] = useState<PendingCorrection[] | null>(null);
  const [contradictions, setContradictions] = useState<Contradiction[] | null>(null);
  const [questions, setQuestions] = useState<OwnerQuestion[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  // INS-7 — a refused approve-all. Held here rather than in the bar, because the reload that
  // follows can move the bar under a card that was not there before, which remounts it.
  const [stale, setStale] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [pending, proposed, disagreeing] = await Promise.all([
        api.listReview(),
        api.listCorrections(),
        api.listContradictions(),
      ]);
      setInsights(pending);
      setCorrections(proposed);
      setContradictions(disagreeing);
      setProblem(null);

      // Read on its own, after the three above: a question is never blocking, and a queue that
      // could not read its questions still holds what must be decided.
      try {
        setQuestions(await api.listQuestions());
      } catch {
        setQuestions([]);
      }
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The review queue could not be loaded.");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  // Drop the card locally the moment it is decided, and refresh the rail badge — the queue
  // is meant to shrink as the owner works down it, not blink through a whole reload.
  const remove = useCallback(
    (...ids: string[]) => {
      setInsights((current) => (current ? current.filter((insight) => !ids.includes(insight.id)) : current));
      refreshReview();
    },
    [refreshReview],
  );

  const answered = useCallback(
    (id: string) => {
      setContradictions((current) => (current ? current.filter((card) => card.id !== id) : current));
      refreshReview();
    },
    [refreshReview],
  );

  const resolved = useCallback(
    (id: string) => {
      setCorrections((current) => (current ? current.filter((card) => card.id !== id) : current));
      refreshReview();
    },
    [refreshReview],
  );

  const asked = useCallback(
    (id: string) => {
      setQuestions((current) => current.filter((question) => question.id !== id));
      refreshReview();
    },
    [refreshReview],
  );

  const edit = useCallback((id: string, normalizedText: string) => {
    setInsights((current) =>
      current ? current.map((insight) => (insight.id === id ? { ...insight, normalizedText } : insight)) : current,
    );
  }, []);

  const type = params.get("type") ?? "all";
  const shownInsights = type === "all" || type === "insights" ? (insights ?? []) : [];
  const shownCorrections = type === "all" || type === "corrections" ? (corrections ?? []) : [];
  const shownContradictions = type === "all" || type === "contradictions" ? (contradictions ?? []) : [];
  const shownQuestions = type === "all" || type === "questions" ? questions : [];
  const loading = insights === null || corrections === null || contradictions === null;

  return (
    <div className="mx-auto flex max-w-[820px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-col gap-[var(--space-1)]">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Review queue
        </h1>
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          Nothing becomes truth until you say so. Approve, improve or discard.
        </p>
      </header>

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      {stale && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {stale}
        </p>
      )}

      {loading ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : shownInsights.length === 0
        && shownCorrections.length === 0
        && shownContradictions.length === 0
        && shownQuestions.length === 0 ? (
        <EmptyState filtered={type !== "all"} />
      ) : (
        <ul className="flex flex-col gap-[var(--space-4)]">
          {shownInsights.map((insight) => {
            // Placed by where the source's last card is, not by what follows this one: a card that
            // arrived later can sit elsewhere in the list, and a source still gets one bar.
            const ofSource = shownInsights.filter((card) => card.sourceMaterialId === insight.sourceMaterialId);
            const lastOfSource = ofSource[ofSource.length - 1]?.id === insight.id;

            return (
              <li key={insight.id} className="flex flex-col gap-[var(--space-4)]">
                <InsightCard insight={insight} onDecided={() => remove(insight.id)} onImproved={edit} />

                {/* INS-7 — under the last card of a source, and only when it has more than one:
                    a single card already has its own Approve. */}
                {lastOfSource && ofSource.length > 1 && (
                  <ApproveSourceBar
                    sourceId={insight.sourceMaterialId}
                    sourceName={insight.sourceTitle}
                    pendingIds={ofSource.map((card) => card.id)}
                    onApproved={() => {
                      setStale(null);
                      remove(...ofSource.map((card) => card.id));
                    }}
                    onStale={(message) => {
                      setStale(message);
                      void load();
                    }}
                  />
                )}
              </li>
            );
          })}

          {shownCorrections.map((correction) => (
            <li key={correction.id}>
              <CorrectionCard correction={correction} onResolved={() => resolved(correction.id)} />
            </li>
          ))}

          {shownContradictions.map((card) => (
            <li key={card.id}>
              <ContradictionCard card={card} onAnswered={() => answered(card.id)} />
            </li>
          ))}

          {shownQuestions.map((question) => (
            <li key={question.id}>
              <QuestionCard
                question={question}
                onAnswered={(outcome) => {
                  asked(question.id);
                  // An answer can close more than its own card — a role places a person, so their
                  // team card goes too — and only the Server's sweep knows which.
                  if (!outcome.dismissed) void load();
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * UI-21 — one question, in the queue's card frame. The question and its one answer shape are the
 * shared component's; this only draws the badge and says where it was noticed.
 */
function QuestionCard({
  question,
  onAnswered,
}: {
  question: OwnerQuestion;
  onAnswered: (outcome: { dismissed: boolean }) => void;
}) {
  return (
    <article className="relative flex flex-col gap-[var(--space-3)] focus-within:z-10 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-6)] py-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex flex-wrap items-center gap-[var(--space-3)]">
        <span
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] px-2.5 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)]"
          style={{ background: "color-mix(in oklch, var(--vl-life) 18%, transparent)", color: "var(--vl-life)" }}
        >
          <CircleHelp size={11} strokeWidth={2.5} />
          Question
        </span>
        <span className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          {question.sourceTitle ? `From ${question.sourceTitle}` : "Noticed in your world view"} ·{" "}
          {formatDate(question.createdAt)}
        </span>
        {question.subjectSlug && (
          <Link
            to={`/world-view/${question.subjectSlug}`}
            className="ml-auto text-[length:var(--text-xs)] text-[var(--text-muted)] underline-offset-2 hover:text-[var(--text-primary)] hover:underline"
          >
            Open the page
          </Link>
        )}
      </div>

      <QuestionAnswer question={question} onAnswered={onAnswered} />
    </article>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] bg-[var(--surface-glass)] px-[var(--space-8)] py-[var(--space-12)] text-center backdrop-blur-[var(--blur-sm)]">
      <CheckCheck size={32} strokeWidth={2} className="text-[var(--vl-life-soft)]" aria-hidden />
      <p className="mt-[var(--space-3)] text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
        All caught up
      </p>
      <p className="mt-1 max-w-[42ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        {filtered ? (
          <>
            Nothing of that kind is waiting.{" "}
            <Link to="/review" className="underline underline-offset-2 hover:text-[var(--text-primary)]">
              Show everything
            </Link>
            .
          </>
        ) : (
          "Every insight has been reviewed, every wording fix answered, nothing you hold disagrees with itself, and nothing is waiting to be asked. New items land here as your sources are processed."
        )}
      </p>
    </div>
  );
}

/**
 * AUTO-1 — one contradiction, and the three answers to it.
 *
 * The two beliefs are drawn side by side and identically, because the screen must not imply
 * which one is right. "Keep this one" is the resolution the milestone insists reuses WORLD-1's
 * mechanics rather than a parallel path: the other belief's window closes and points at the
 * one kept, exactly as if synthesis had decided it, and the world view's files catch up on the
 * next batch.
 *
 * "Acknowledge both" is not a way of dismissing the card. It is the answer for a disagreement
 * that is real in the world — both beliefs go on standing, and both go on wearing the
 * contradiction flag on the subject page, which is where a real disagreement belongs.
 */
function ContradictionCard({ card, onAnswered }: { card: Contradiction; onAnswered: () => void }) {
  const { api } = useApp();

  const [busy, setBusy] = useState<ContradictionAction | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function answer(action: ContradictionAction, keepBeliefId?: string) {
    if (busy) return;
    setBusy(action);
    setProblem(null);
    try {
      await api.resolveContradiction(card.id, action, keepBeliefId);
      onAnswered();
    } catch (cause: unknown) {
      setProblem(
        cause instanceof ApiError && cause.status === 404
          ? "That contradiction has already been answered, or one of its beliefs has since been superseded."
          : "That could not be saved. Try again.",
      );
      setBusy(null);
    }
  }

  return (
    <article className="relative flex flex-col gap-[var(--space-3)] focus-within:z-10 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-6)] py-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex flex-wrap items-center gap-[var(--space-3)]">
        <span
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] px-2.5 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)]"
          style={{
            background: "color-mix(in oklch, var(--warning) 18%, transparent)",
            color: "var(--warning)",
          }}
        >
          <GitFork size={11} strokeWidth={2.5} />
          Contradiction
        </span>

        {/* Where the pair came from. "An insight disputed this" and "nothing had ever compared
            these two" are different things to know when deciding between them. */}
        <span className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          {card.origin === "challenge"
            ? `Synthesis batch ${card.batchId?.slice(0, 8) ?? "—"}`
            : "Scheduled sweep"}{" "}
          · {formatDate(card.createdAt)}
        </span>
      </div>

      <Link
        to={`/world-view/${card.subjectSlug}`}
        className="w-fit text-[length:var(--text-lg)] font-bold text-[var(--text-primary)] underline-offset-4 hover:underline"
      >
        {card.subjectTitle}
      </Link>

      <div className="grid gap-[var(--space-3)] sm:grid-cols-2">
        <Side label="Belief A" belief={card.beliefA} busy={busy !== null} onKeep={() => void answer("keep", card.beliefA.id)} />
        <Side label="Belief B" belief={card.beliefB} busy={busy !== null} onKeep={() => void answer("keep", card.beliefB.id)} />
      </div>

      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
        Acknowledging keeps both current — some contradictions are real in the world.
      </p>

      {problem && (
        <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <div className="flex items-center justify-end gap-[var(--space-2)]">
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void answer("discard")}
          className="rounded-[var(--radius-md)] px-[var(--space-3)] py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--text-secondary)] transition hover:text-[var(--text-primary)] disabled:opacity-50"
        >
          {busy === "discard" ? "Discarding…" : "Discard"}
        </button>
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void answer("acknowledge")}
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-[var(--space-4)] py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          <Eye size={14} strokeWidth={2.5} />
          {busy === "acknowledge" ? "Acknowledging…" : "Acknowledge both"}
        </button>
      </div>
    </article>
  );
}

function Side({
  label,
  belief,
  busy,
  onKeep,
}: {
  label: string;
  belief: ContradictionBelief;
  busy: boolean;
  onKeep: () => void;
}) {
  return (
    <div className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-1)] p-[var(--space-4)]">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          {label}
        </span>
        {belief.confidence !== null && (
          <span className="font-mono text-[length:var(--text-2xs)] text-[var(--text-muted)]">
            confidence {belief.confidence.toFixed(2)}
          </span>
        )}
      </div>

      <p className="text-[length:var(--text-sm)] leading-relaxed text-[var(--text-primary)]">{belief.statement}</p>

      {/* Where it was read from, which is most of what somebody has to go on when the two
          statements are equally plausible. It carries the insight, so following it lands on
          the lines it was drawn from rather than at the top of an hour of transcript (UI-7). */}
      {belief.sourceMaterialId && (
        <Link
          to={
            (belief.sourceKind === "recording"
              ? `/recordings/${belief.sourceMaterialId}`
              : `/sources/${belief.sourceMaterialId}`) +
            (belief.insightId ? `?insight=${belief.insightId}` : "")
          }
          className="inline-flex w-fit items-center gap-1.5 text-[length:var(--text-2xs)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
        >
          <Link2 size={11} strokeWidth={2} />
          {belief.sourceTitle ?? "the source"}
          {belief.sourceDate && <> · {formatDate(belief.sourceDate)}</>}
        </Link>
      )}

      <button
        type="button"
        disabled={busy}
        onClick={onKeep}
        className="mt-auto inline-flex w-fit items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-3)] py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)] disabled:opacity-50"
      >
        <Check size={13} strokeWidth={2.5} />
        Keep this one
      </button>
    </div>
  );
}

/**
 * SPCH-8 — a wording fix the `Reasoner` proposed and could not apply on its own.
 *
 * **Two answers, not three.** The design draws Discard beside Keep original, which on a
 * proposal are the same act twice: the Server has one route with one boolean, and "do not
 * apply this" is what both would send. So the card offers applying it and keeping what was
 * heard, and nothing that looks like a third outcome.
 *
 * The link out is the point of the card rather than an afterthought. A correction is
 * judgeable from its diff most of the time and from the surrounding lines the rest of it, so
 * the source line opens Transcript Review — where the same proposal is waiting, because both
 * screens read and resolve one row.
 */
function CorrectionCard({
  correction,
  onResolved,
}: {
  correction: PendingCorrection;
  onResolved: () => void;
}) {
  const { api } = useApp();

  const [busy, setBusy] = useState<"apply" | "keep" | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function resolve(apply: boolean) {
    if (busy) return;
    setBusy(apply ? "apply" : "keep");
    setProblem(null);
    try {
      await api.resolveProposal(correction.sourceMaterialId, correction.id, apply);
      onResolved();
    } catch (cause: unknown) {
      setProblem(
        cause instanceof ApiError && cause.status === 404
          ? "That correction has already been answered somewhere else."
          : "That could not be saved. Try again.",
      );
      setBusy(null);
    }
  }

  return (
    <article className="relative flex flex-col gap-[var(--space-3)] focus-within:z-10 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-6)] py-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex flex-wrap items-center gap-[var(--space-3)]">
        <span
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] px-2.5 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)]"
          style={{
            background: "color-mix(in oklch, var(--vl-bridge) 18%, transparent)",
            color: "var(--vl-bridge)",
          }}
        >
          <SpellCheck size={11} strokeWidth={2.5} />
          Correction
        </span>

        {/* Straight to Transcript Review rather than to the recording: this is the one card
            whose context is the lines around it, and that is the screen that has them. */}
        <Link
          to={`/recordings/${correction.sourceMaterialId}/review`}
          className="inline-flex items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
        >
          <Link2 size={12} strokeWidth={2} />
          {correction.sourceTitle ?? "the source"}
          {correction.startsAtMs !== null && <> · {formatTimecode(correction.startsAtMs)}</>}
        </Link>
      </div>

      <div className="flex flex-col gap-1.5 text-[length:var(--text-sm)]">
        <p className="text-[var(--text-muted)] line-through">{correction.originalText}</p>
        <p className="text-[var(--vl-life-soft)]">{correction.correctedText}</p>
        {correction.reasoning && (
          <p className="text-[length:var(--text-xs)] italic text-[var(--text-muted)]">{correction.reasoning}</p>
        )}
      </div>

      {problem && (
        <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <div className="flex items-center justify-end gap-[var(--space-2)]">
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void resolve(false)}
          className="rounded-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-3)] py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)] disabled:opacity-50"
        >
          {busy === "keep" ? "Keeping…" : "Keep original"}
        </button>
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void resolve(true)}
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-[var(--space-4)] py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          <Check size={14} strokeWidth={2.5} />
          {busy === "apply" ? "Applying…" : "Approve"}
        </button>
      </div>
    </article>
  );
}

function InsightCard({
  insight,
  onDecided,
  onImproved,
}: {
  insight: ReviewInsight;
  onDecided: () => void;
  onImproved: (id: string, normalizedText: string) => void;
}) {
  const { api, refreshWorldViewActivity } = useApp();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(insight.normalizedText);
  const [busy, setBusy] = useState<"approve" | "discard" | "improve" | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const meta = categoryMeta(insight.category);
  // UI-7 — the link carries the insight, so the source lands on the transcript with the
  // lines this insight was drawn from highlighted, not merely at the recording's top.
  const sourceBase =
    insight.sourceKind === "recording" ? `/recordings/${insight.sourceMaterialId}` : `/sources/${insight.sourceMaterialId}`;
  const sourceHref = `${sourceBase}?insight=${insight.id}`;
  const sourceName = insight.sourceTitle ?? "the source";

  async function decide(action: "approve" | "discard") {
    if (busy) return;
    setBusy(action);
    setProblem(null);
    try {
      if (action === "approve") {
        // UI-23 — start polling the world view's activity right away rather than
        // waiting for its next natural tick, when this approval was the one that
        // started (or is about to start) a pass.
        const { synthesizing } = await api.approveInsight(insight.id);
        if (synthesizing) refreshWorldViewActivity();
      } else {
        await api.discardInsight(insight.id);
      }
      onDecided();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved. Try again.");
      setBusy(null);
    }
  }

  async function saveImprove() {
    const text = draft.trim();
    if (busy || text === "") return;
    setBusy("improve");
    setProblem(null);
    try {
      const { normalizedText } = await api.improveInsight(insight.id, text);
      onImproved(insight.id, normalizedText);
      // Resync so a second Improve opens what the card now shows, not the pre-normalized
      // text that was typed the first time.
      setDraft(normalizedText);
      setEditing(false);
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved. Try again.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <article className="relative flex flex-col gap-[var(--space-3)] focus-within:z-10 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-6)] py-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex flex-wrap items-center gap-[var(--space-3)]">
        <span
          className="rounded-[var(--radius-full)] px-2.5 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)]"
          style={{ background: `color-mix(in oklch, ${meta.colour} 18%, transparent)`, color: meta.colour }}
        >
          {meta.label}
        </span>

        <Link
          to={sourceHref}
          className="inline-flex items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
        >
          <Link2 size={12} strokeWidth={2} />
          {sourceName}
          {insight.sourceDate && <> · {formatDate(insight.sourceDate)}</>}
        </Link>

        {/* CAP-8 — approving this lands it as the owner's own word, pinned, so the card says so
            before the owner decides rather than after. */}
        {insight.sourceAuthoredByOwner && (
          <span className="inline-flex items-center gap-1 text-[length:var(--text-xs)] font-medium text-[var(--vl-bridge)]">
            <PenLine size={11} strokeWidth={2} aria-hidden />
            Written by you
          </span>
        )}
      </div>

      {editing ? (
        <div className="flex flex-col gap-[var(--space-2)]">
          <textarea
            value={draft}
            autoFocus
            rows={3}
            onChange={(event) => setDraft(event.target.value)}
            className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-md)] leading-relaxed text-[var(--text-primary)] outline-none focus:border-[var(--vl-bridge)]"
          />
          <div className="flex items-center gap-[var(--space-2)]">
            <button
              type="button"
              disabled={busy !== null || draft.trim() === ""}
              onClick={() => void saveImprove()}
              className="rounded-[var(--radius-md)] bg-[var(--primary)] px-[var(--space-3)] py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
            >
              {busy === "improve" ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(insight.normalizedText);
                setEditing(false);
              }}
              className="rounded-[var(--radius-md)] px-[var(--space-3)] py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)] transition hover:text-[var(--text-primary)]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="text-[length:var(--text-md)] leading-relaxed text-[var(--text-primary)]">
          {insight.normalizedText}
        </p>
      )}

      {insight.entities.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {insight.entities.map((entity) => (
            <span
              key={entity.id}
              className="rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-2 py-0.5 text-[length:var(--text-2xs)] font-medium text-[var(--text-secondary)]"
            >
              {entity.name}
            </span>
          ))}
        </div>
      )}

      {/* INS-1 — the extractor could not name something this sentence leans on. An insight
          is read on its own everywhere after this screen, so an unresolved "the system" here
          becomes a belief about nothing in the world view. Improving the text clears it. */}
      {!insight.grounded && (
        <p className="flex items-start gap-1.5 rounded-[var(--radius-md)] border border-[var(--warning)]/40 bg-[var(--warning)]/10 px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          <AlertTriangle size={13} strokeWidth={2} className="mt-0.5 shrink-0 text-[var(--warning)]" aria-hidden />
          <span>
            {describeUngrounded(insight.ungroundedReasons) ||
              "Something this insight refers to could not be named from the source."}{" "}
            Improve it before approving, or it will be believed about nothing in particular.
          </span>
        </p>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="inline-flex w-fit items-center gap-1.5 text-[length:var(--text-xs)] font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
      >
        <ChevronDown
          size={13}
          strokeWidth={2}
          className={cn("transition-transform", open && "rotate-180")}
        />
        How this was read
      </button>

      {open && <HowItWasRead insight={insight} />}

      {problem && (
        <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <div className="flex items-center justify-end gap-[var(--space-2)]">
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void decide("discard")}
          className="rounded-[var(--radius-md)] px-[var(--space-3)] py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--text-secondary)] transition hover:text-[var(--text-primary)] disabled:opacity-50"
        >
          {busy === "discard" ? "Discarding…" : "Discard"}
        </button>
        <button
          type="button"
          disabled={busy !== null || editing}
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-3)] py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)] disabled:opacity-50"
        >
          <Pencil size={13} strokeWidth={2} />
          Improve
        </button>
        <button
          type="button"
          disabled={busy !== null || editing}
          onClick={() => void decide("approve")}
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-[var(--space-4)] py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          <Check size={14} strokeWidth={2.5} />
          {busy === "approve" ? "Approving…" : "Approve"}
        </button>
      </div>
    </article>
  );
}

/**
 * The step breakdown behind "How this was read" — the composed then normalized text (INS-1),
 * the subject the insight is about and the references grounding resolved to get there, the
 * source lines it was drawn from, and the candidate relations approving accepts. This is the
 * design's disclosure, and it is how the owner learns whether to trust the extractor.
 */
function HowItWasRead({ insight }: { insight: ReviewInsight }) {
  return (
    <div className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-1)] p-[var(--space-4)]">
      {insight.composedText !== insight.normalizedText && (
        <Field label="Composed" value={insight.composedText} />
      )}
      <Field label="Normalized (English)" value={insight.normalizedText} />
      {insight.subject && <Field label="About" value={insight.subject} />}
      <Field label="Category" value={categoryLabel(insight.category)} />

      {/* What grounding resolved to make the insight stand on its own (INS-1) — a pronoun
          that became a name, "voor vrijdag" that became a date, a bare "the system" that
          became the system. The owner is judging the resolution as much as the claim. */}
      {insight.grounding.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
            Grounded
          </p>
          {insight.grounding.map((reference, index) => (
            <p key={index} className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              “{reference.expression}” → <span className="text-[var(--text-primary)]">{reference.referent}</span>
            </p>
          ))}
        </div>
      )}

      {insight.quotes.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
            Drawn from
          </p>
          {insight.quotes.map((quote, index) => (
            <p
              key={index}
              className="border-l-2 border-[var(--border-default)] pl-[var(--space-3)] text-[length:var(--text-sm)] italic text-[var(--text-secondary)]"
            >
              {quote}
            </p>
          ))}
        </div>
      )}

      {insight.relations.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
            Relations
          </p>
          {insight.relations.map((relation, index) => (
            <p key={index} className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              <span className="font-medium text-[var(--text-primary)]">{relation.subject}</span>{" "}
              {relation.relationType.replace(/_/g, " ")}{" "}
              <span className="font-medium text-[var(--text-primary)]">{relation.object}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        {label}
      </p>
      <p className="text-[length:var(--text-sm)] text-[var(--text-primary)]">{value}</p>
    </div>
  );
}
